import React, { useState } from 'react';
import '../styles/LoginScreen.css';
import { ReactComponent as Logo } from '../assets/LogoNidouxSVG.svg';
import { ReactComponent as LogoClosed } from '../assets/LogoNidouxSVGClosed.svg';
import LanguageSwitcher from '../components/shared/LanguageSwitcher'; 
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getAppIdentifier } from '../config';
import { useBodyBackground } from '../hooks/useBodyBackground';

function LoginScreen({ onLoginSuccess, onShowTerms }) {
  useBodyBackground('#ffffff');
  const { t } = useTranslation();
  const [accessCode, setAccessCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [showSmsInput, setShowSmsInput] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const appId = getAppIdentifier();
      const response = await fetch(`${getApiBaseUrl()}/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-App-Identifier': appId || ''
        },
        credentials: 'include', // ✅ This is all you need - browser/WebView handles cookies
        body: JSON.stringify({ accessCode })
      });

      if (response.ok) {
        const data = await response.json();
        
        // ✅ REMOVED manual cookie handling - let browser do it automatically
        // The 'credentials: include' option ensures cookies are sent/received
        
        if (data.smsVerificationRequired && !data.isOwner && !data.isAdmin) {
          setPendingUserId(data.userId);
          setShowSmsInput(true);
          setError('');
        } else {
          onLoginSuccess(data);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('login.accessDenied'));
      }
    } catch (err) {
      setError(t('login.networkError'));
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSmsCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const appId = getAppIdentifier();
      const response = await fetch(`${getApiBaseUrl()}/login/verify-sms-code`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-App-Identifier': appId || ''
        },
        credentials: 'include', // ✅ This is all you need
        body: JSON.stringify({ userId: pendingUserId, code: smsCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // ✅ REMOVED manual cookie handling
        
        onLoginSuccess(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('login.verificationFailed'));
      }
    } catch (err) {
      setError(t('login.networkError'));
      console.error('SMS verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowSmsInput(false);
    setSmsCode('');
    setPendingUserId(null);
    setError('');
  };

  const handleFocus = () => {
    setIsKeyboardOpen(true);
  };

  const handleBlur = () => {
    setIsKeyboardOpen(false);
  };

  return (
    <div className={`login-page ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
      <div className="language-switcher-wrapper">
        <LanguageSwitcher />
      </div>

      <div className="logo-container">
        {accessCode.length > 0 ? (
          <LogoClosed 
            key="closed"
            className="logo-svg"
          />
        ) : (
          <Logo 
            key="open"
            className="logo-svg"
          />
        )}
      </div>

      <p className="app-subtitle">{t('login.subtitle')}</p>

      {!showSmsInput ? (
        <form onSubmit={handleAccessCodeSubmit} className="login-form">
          <div className="input-group">
            <label htmlFor="accessCode" className="input-label">
              {t('login.accessCode')}
            </label>
            <input
              type="password"
              id="accessCode"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={t('login.accessCodePlaceholder')}
              className="input-field"
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
         
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner-small"></span>
                {t('login.signingIn')}
              </span>
            ) : (
              t('login.signIn')
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSmsCodeSubmit} className="login-form">
          <div className="sms-notice">
            <span className="sms-icon">📱</span>
            <p>{t('login.smsNotice')}</p>
          </div>

          <div className="input-group">
            <label htmlFor="smsCode" className="input-label">
              {t('login.verificationCode')}
            </label>
            <input
              type="text"
              id="smsCode"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={t('login.verificationCodePlaceholder')}
              className="input-field"
              maxLength="6"
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
             
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner-small"></span>
                {t('login.verifying')}
              </span>
            ) : (
              t('login.verify')
            )}
          </button>

          <button
            type="button"
            onClick={handleBackToLogin}
            className="back-button"
            disabled={loading}
          >
            ← {t('login.backToLogin')}
          </button>
        </form>
      )}

      <div className="login-footer">
        <p className="powered-by">
          {t('login.poweredBy')}
          {onShowTerms && (
            <>
              {' • '}
              <button 
                type="button"
                onClick={onShowTerms}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                {t('login.termsAndConditions')}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

export default LoginScreen;